import React, { useState } from 'react';
import { Save, X, Clock } from 'lucide-react';
import { ActionType, MoldOrder, OrderDetail } from '../../types/mold';
import NextStepActionBox from './NextStepActionBox';
import { Tabs } from 'radix-ui';
import { ApprovalHistoryTab } from '../MoldDetail/ApprovalHistoryTab';
import AuditLogsPanel from '../shared/AuditLogsPanel';
import MoldOrderHeader from './Header';

import { PatchMoldOrderRequest } from '../../types/moldOrder';
import FormField from '../shared/FormField';
import { useAppState } from '../../context/AppStateContext';
import { DisplayFieldConfig } from '../../types/form';
import { getNestedValue } from '../../utils/object';
import NewStepProgressBar from './NewStepProgressBar';
import CommentsPanel from './CommentsPanel';

interface MoldOrderDetailProps {
  moldOrder: MoldOrder;
  handleStatusUpdate: (action: ActionType, assignedTo?: number | null) => Promise<void>;
  updateMoldOrderStateInstance: (moldOrder: MoldOrder, data: { notes?: string }) => Promise<void>;
  updateMoldOrder: (data: PatchMoldOrderRequest) => Promise<void>;
  updateMoldOrderDetail: (orderDetail?: Partial<OrderDetail>) => Promise<void>;
  downloadPdfReport: (id: number) => Promise<Blob | null>;
}

const FieldItem: React.FC<{
  field: DisplayFieldConfig;
  moldOrder: MoldOrder;
  onUpdateField: (data: Partial<OrderDetail>, entityToUpdate?: 'moldOrder' | 'orderDetail') => void;
}> = ({
  field,
  moldOrder,
  onUpdateField,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [tempValue, setTempValue] = useState<any>(null);
    const [tempCustomOtherValue, setTempCustomOtherValue] = useState<any>(null);
    const [isValidInput, setIsValidInput] = useState(true);
    const [error, setError] = useState('');
    if (field.conditionToDisplay && !field.conditionToDisplay(moldOrder)) {
      return null;
    }

    const editable = typeof field.editable === 'function'
      ? field.editable(moldOrder)
      : field.editable;

    // Extract field value based on fieldName path
    const getFieldValue = (moldOrder: MoldOrder, path: string, entityToUpdate?: 'moldOrder' | 'orderDetail'): any => {
      if (entityToUpdate === 'orderDetail') {
        return getNestedValue(moldOrder.orderDetail, path);
      }
      return getNestedValue(moldOrder, path);
    };

    const getCustomFieldName = (fieldName: string): keyof OrderDetail => {
      return `custom${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)}` as keyof OrderDetail;
    };

    const handleEdit = () => {
      if (field.fieldName) {
        // Initialize tempValue with current value when entering edit mode
        const tempValue = getFieldValue(moldOrder, field.fieldName, field.editConfig?.entityToUpdate ?? 'orderDetail');
        setTempValue(tempValue);
        if (tempValue === 'other_custom_text') {
          const tempCustomValue = getFieldValue(moldOrder, getCustomFieldName(field.fieldName), 'orderDetail')
          setTempCustomOtherValue(tempCustomValue);
        }
        setIsEditing(true);
      }
    };

    const handleCancel = () => {
      setIsEditing(false);
      setTempValue(null);
    };

    const handleChange = (value: any) => {
      setTempValue(value);
      setTempCustomOtherValue('');
      setError('')
    };

    const handleCustomValueChange = (value: any) => {
      setTempCustomOtherValue(value);
      setError('')
    };

    const handleSave = () => {
      if (field.fieldName && tempValue !== null && (typeof tempValue === 'string' ? tempValue.trim() !== '' : true)) {
        const customValue = tempValue === 'other_custom_text' ? tempCustomOtherValue : '';
        if (tempValue === 'other_custom_text' && (!tempCustomOtherValue || tempCustomOtherValue.trim() === '')) {
          setError('กรุณาระบุข้อมูล');
          return;
        }

        // Handle Date type for tempValue
        const formattedValue = tempValue instanceof Date ? tempValue.toISOString() : tempValue;

        onUpdateField({
          [field.fieldName]: formattedValue,
          ...(customValue ? { [getCustomFieldName(field.fieldName)]: customValue } : {}),
        }, field.editConfig?.entityToUpdate);

        setIsEditing(false);
        setTempValue(null);
      } else {
        setError('กรุณาระบุข้อมูล');
        return;
      }
    };

    return (
      <div
        className="grid grid-cols-2 sm:grid-cols-[250px_1fr] items-center gap-2 py-1.5 sm:py-2 px-3 sm:px-4 text-base"
        data-cy={`mold-order-detail`}>
        <span className="text-gray-500 text-sm sm:text-base">{field.label}:</span>
        <div className="flex items-center w-full">
          {field.icon}
          {editable ? (
            <div className="flex items-center w-full">
              {isEditing ? (
                <div className="flex flex-col sm:flex-row items-start w-full gap-1 sm:gap-2">
                  <div className="flex flex-col gap-2">
                    <FormField
                      {...field.editConfig}
                      required={typeof field.editConfig?.required === 'function'
                        ? field.editConfig.required(moldOrder)
                        : field.editConfig?.required}
                      computedConfig={undefined}
                      key={String(field.fieldName)}
                      formType={field.editConfig?.type}
                      value={tempValue !== null ? tempValue : (field.fieldName ? getFieldValue(moldOrder, field.fieldName, field.editConfig?.entityToUpdate) : '')}
                      onChange={handleChange}
                      className="mr-2"
                      testIdIdentifier={field.fieldName || 'inputField'}
                      onChangeInputValidity={(valid: boolean) => { console.log(valid); setIsValidInput(valid); }}
                      autoFocus
                    />
                    {field.fieldName && field.editConfig?.type === 'select_with_other' && tempValue === 'other_custom_text' && (
                      <FormField
                        formType='text'
                        value={tempCustomOtherValue !== null ? tempCustomOtherValue : (field.fieldName ? getFieldValue(moldOrder, getCustomFieldName(field.fieldName)) : '')}
                        onChange={handleCustomValueChange}
                        className={`mr-2 ${error ? 'border-red-500' : ''}`}
                        placeholder={`โปรดระบุ${field.label}`}
                        testIdIdentifier={getCustomFieldName(field.fieldName)}
                        autoFocus
                      />
                    )}
                    {error && (<div className="text-red-500 text-sm">{error}</div>)}
                  </div>
                  <div className="flex flex-row gap-1 sm:gap-1">
                    <button
                      className={
                        isValidInput
                          ? "p-3 bg-green-100 text-green-600 rounded-md hover:bg-green-200 transition-colors"
                          : "p-3 bg-gray-200 text-gray-600 rounded-md"
                      }
                      disabled={!isValidInput}
                      onClick={handleSave}
                      title="บันทึก"
                    >
                      <Save size={16} />
                    </button>
                    <button
                      className="p-3 bg-red-100 text-red-500 rounded-md hover:bg-red-200 transition-colors"
                      onClick={handleCancel}
                      title="ยกเลิก"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center space-x-2 w-full">
                  <div>{field.getValue(moldOrder)}</div>
                  <button
                    className="ml-2 text-xs text-blue-500 hover:text-blue-700"
                    onClick={handleEdit}
                  >
                    แก้ไข
                  </button>
                </div>
              )}
            </div>
          ) : (
            field.getValue(moldOrder)
          )}
        </div>
      </div>
    );
  };


export const MoldOrderDetail: React.FC<MoldOrderDetailProps> = ({
  moldOrder,
  handleStatusUpdate,
  updateMoldOrder,
  updateMoldOrderDetail,
  updateMoldOrderStateInstance,
  downloadPdfReport,
}) => {
  const { getDisplayMoldOrderFields } = useAppState();
  const fields = getDisplayMoldOrderFields(moldOrder.type);
  const handleUpdateField = async (
    data: Partial<OrderDetail>,
    entityToUpdate: 'moldOrder' | 'orderDetail' = 'orderDetail'
  ) => {
    try {
      if (entityToUpdate === 'moldOrder') {
        await updateMoldOrder(data);
      } else {
        await updateMoldOrderDetail(data);
      }
    } catch (error) {
      alert('ขออภัยเกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง');
      console.error('Failed to update fields:', error);
    }
  };
  const { featureToggles } = useAppState();
  const enableMultiApprovers = featureToggles.enableMultiApprovers;
  const tabTwClasses = {
    container: "mt-3 sm:mt-6",
    list: "flex space-x-2 border-b border-gray-200 mb-3",
    trigger: "py-2 px-3 text-sm font-medium text-gray-700 data-[state=active]:border-b-2 data-[state=active]:border-blue-500",
  }

  return (
    <div className="w-full px-4 py-2 sm:p-4">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 relative overflow-hidden">
        <div className="p-4 sm:p-6 relative z-10">
          {/* Header Section */}
          <MoldOrderHeader moldOrder={moldOrder} />

          <div className="space-y-4 lg:space-y-0 lg:flex lg:gap-6">
            <div className="flex-auto w-full xl:w-2/3">
              {/* Info Section */}
              <div className="bg-gray-50 rounded-lg mb-3 sm:mb-4">
                <div className="divide-y divide-gray-200">
                  {fields.map((field, index) => (
                    <FieldItem
                      key={index}
                      field={field}
                      moldOrder={moldOrder}
                      onUpdateField={handleUpdateField}
                    />
                  ))}
                </div>
              </div>

              {/* Note Section */}
              {moldOrder.orderDetail.note && (
                <div className="mb-3 sm:mb-4">
                  <div className="text-base text-gray-500 mb-1">
                    คำสั่งเพิ่มเติม:
                  </div>
                  <div className="bg-gray-50 p-2.5 sm:p-3 rounded-md text-gray-700 text-lg">
                    {moldOrder.orderDetail.note === 'other_custom_text'
                      ? moldOrder.orderDetail.customNote
                      : moldOrder.orderDetail.note}
                  </div>
                </div>
              )}
            </div>

            <NextStepActionBox
              moldOrder={moldOrder}
              onStatusUpdate={handleStatusUpdate}
              updateMoldOrder={updateMoldOrder}
              updateMoldOrderStateInstance={updateMoldOrderStateInstance}
              updateMoldOrderDetail={updateMoldOrderDetail}
              downloadPdfReport={downloadPdfReport}
            />
          </div>

          <div className={tabTwClasses.container}>
            <Tabs.Root defaultValue="progress">
              <Tabs.List className={tabTwClasses.list}>
                <Tabs.Trigger value="progress" className={tabTwClasses.trigger}>สถานะใบงาน</Tabs.Trigger>
                {enableMultiApprovers && <Tabs.Trigger value="approval" className={tabTwClasses.trigger}>ประวัติการอนุมัติ</Tabs.Trigger>}
                <Tabs.Trigger value="audit" className={tabTwClasses.trigger}>Audit logs</Tabs.Trigger>
              </Tabs.List>

              <Tabs.Content value="progress">
                <div className="relative">
                  <div className="overflow-auto sm:overflow-x-auto pb-4 -mx-4 px-4 sm:mx-0 sm:px-0">
                    <NewStepProgressBar moldOrder={moldOrder} />
                  </div>
                </div>
              </Tabs.Content>

              {enableMultiApprovers && (
                <Tabs.Content value="approval">
                  <div className="bg-white rounded-md p-3">
                    <ApprovalHistoryTab moldOrder={moldOrder} />
                  </div>
                </Tabs.Content>
              )}

              <Tabs.Content value="audit">
                <div className="bg-white rounded-md p-3">
                  <h2 className="text-xl font-bold mb-4 flex items-center">
                    <Clock className="mr-2" size={20} />
                    Audit logs
                  </h2>
                  <AuditLogsPanel entityType="mold_order" entityId={moldOrder.id} />
                </div>
              </Tabs.Content>
            </Tabs.Root>
          </div>

          <CommentsPanel moldOrderId={moldOrder.id} />

        </div>
      </div>
    </div>
  );
};

export default MoldOrderDetail;
